import { supabase } from "@/lib/supabaseClient";
import { useState, useEffect } from "react";
import { toast } from "sonner";

export interface Department {
  id: string;
  name: string;
  color: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export interface Position {
  id: string;
  department_id: string;
  name: string;
  description: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
}

export interface Market {
  id: string;
  name: string;
  country: string;
  flag_emoji: string;
  salary_multiplier: number;
  work_week_hours: number;
  annual_leave_days: number;
  social_charges_percent: number;
  is_active: boolean;
  created_at: string;
}

export interface Salary {
  id: string;
  position_id: string;
  market_id: string;
  salary_efficient: number;
  salary_balanced: number;
  salary_luxury: number;
  created_at: string;
}

export interface FTERule {
  id: string;
  position_id: string;
  condition_type: string;
  condition_value: any;
  min_fte: number;
  per_rooms: number | null;
  notes: string;
  is_active: boolean;
  created_at: string;
}

export const useAdminPayroll = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [salaries, setSalaries] = useState<Salary[]>([]);
  const [fteRules, setFteRules] = useState<FTERule[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);

      const [
        deptsResult,
        positionsResult,
        marketsResult,
        salariesResult,
        rulesResult,
      ] = await Promise.all([
        supabase.from("payroll_departments").select("*").order("display_order"),
        supabase.from("payroll_positions").select("*").order("display_order"),
        supabase.from("payroll_markets").select("*").order("name"),
        supabase.from("payroll_salaries").select("*"),
        supabase.from("payroll_fte_rules").select("*").order("created_at"),
      ]);

      if (deptsResult.data) setDepartments(deptsResult.data);
      if (positionsResult.data) setPositions(positionsResult.data);
      if (marketsResult.data) setMarkets(marketsResult.data);
      if (salariesResult.data) setSalaries(salariesResult.data);
      if (rulesResult.data) setFteRules(rulesResult.data);
    } catch (error) {
      console.error("Error loading payroll data:", error);
      toast.error("Failed to load payroll data");
    } finally {
      setLoading(false);
    }
  };

  const addDepartment = async (
    department: Omit<Department, "id" | "created_at">,
  ) => {
    try {
      const { data, error } = await supabase
        .from("payroll_departments")
        .insert([department])
        .select()
        .single();

      if (error) throw error;

      setDepartments((prev) => [...prev, data]);
      toast.success("Department added successfully");
      return data;
    } catch (error) {
      console.error("Error adding department:", error);
      toast.error("Failed to add department");
      throw error;
    }
  };

  const updateDepartment = async (id: string, updates: Partial<Department>) => {
    try {
      const { data, error } = await supabase
        .from("payroll_departments")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      setDepartments((prev) =>
        prev.map((dept) => (dept.id === id ? data : dept)),
      );
      toast.success("Department updated successfully");
      return data;
    } catch (error) {
      console.error("Error updating department:", error);
      toast.error("Failed to update department");
      throw error;
    }
  };

  const deleteDepartment = async (id: string) => {
    try {
      const { error } = await supabase
        .from("payroll_departments")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setDepartments((prev) => prev.filter((dept) => dept.id !== id));
      toast.success("Department deleted successfully");
    } catch (error) {
      console.error("Error deleting department:", error);
      toast.error("Failed to delete department");
      throw error;
    }
  };

  const addPosition = async (position: Omit<Position, "id" | "created_at">) => {
    try {
      const { data, error } = await supabase
        .from("payroll_positions")
        .insert([position])
        .select()
        .single();

      if (error) throw error;

      setPositions((prev) => [...prev, data]);
      toast.success("Position added successfully");
      return data;
    } catch (error) {
      console.error("Error adding position:", error);
      toast.error("Failed to add position");
      throw error;
    }
  };

  const updatePosition = async (id: string, updates: Partial<Position>) => {
    try {
      const { data, error } = await supabase
        .from("payroll_positions")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      setPositions((prev) => prev.map((pos) => (pos.id === id ? data : pos)));
      toast.success("Position updated successfully");
      return data;
    } catch (error) {
      console.error("Error updating position:", error);
      toast.error("Failed to update position");
      throw error;
    }
  };

  const deletePosition = async (id: string) => {
    try {
      const { error } = await supabase
        .from("payroll_positions")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setPositions((prev) => prev.filter((pos) => pos.id !== id));
      toast.success("Position deleted successfully");
    } catch (error) {
      console.error("Error deleting position:", error);
      toast.error("Failed to delete position");
      throw error;
    }
  };

  const addMarket = async (market: Omit<Market, "id" | "created_at">) => {
    try {
      const { data, error } = await supabase
        .from("payroll_markets")
        .insert([market])
        .select()
        .single();

      if (error) throw error;

      setMarkets((prev) => [...prev, data]);
      toast.success("Market added successfully");
      return data;
    } catch (error) {
      console.error("Error adding market:", error);
      toast.error("Failed to add market");
      throw error;
    }
  };

  const updateMarket = async (id: string, updates: Partial<Market>) => {
    try {
      const { data, error } = await supabase
        .from("payroll_markets")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      setMarkets((prev) => prev.map((m) => (m.id === id ? data : m)));
      toast.success("Market updated successfully");
      return data;
    } catch (error) {
      console.error("Error updating market:", error);
      toast.error("Failed to update market");
      throw error;
    }
  };

  const deleteMarket = async (id: string) => {
    try {
      const { error } = await supabase
        .from("payroll_markets")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setMarkets((prev) => prev.filter((m) => m.id !== id));
      toast.success("Market deleted successfully");
    } catch (error) {
      console.error("Error deleting market:", error);
      toast.error("Failed to delete market");
      throw error;
    }
  };

  const upsertSalary = async (
    positionId: string,
    marketId: string,
    salaryData: {
      salary_efficient: number;
      salary_balanced: number;
      salary_luxury: number;
    },
  ) => {
    try {
      const { data, error } = await supabase
        .from("payroll_salaries")
        .upsert({
          position_id: positionId,
          market_id: marketId,
          ...salaryData,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;

      setSalaries((prev) => {
        const existing = prev.find(
          (s) => s.position_id === positionId && s.market_id === marketId,
        );

        if (existing) {
          return prev.map((s) => (s.id === existing.id ? data : s));
        } else {
          return [...prev, data];
        }
      });

      toast.success("Salary updated successfully");
      return data;
    } catch (error) {
      console.error("Error upserting salary:", error);
      toast.error("Failed to update salary");
      throw error;
    }
  };

  const getSalaryEntry = (positionId: string, marketId: string) => {
    return salaries.find(
      (s) => s.position_id === positionId && s.market_id === marketId,
    );
  };

  const addFTERule = async (rule: Omit<FTERule, "id" | "created_at">) => {
    try {
      const { data, error } = await supabase
        .from("payroll_fte_rules")
        .insert([rule])
        .select()
        .single();

      if (error) throw error;

      setFteRules((prev) => [...prev, data]);
      toast.success("FTE rule added successfully");
      return data;
    } catch (error) {
      console.error("Error adding FTE rule:", error);
      toast.error("Failed to add FTE rule");
      throw error;
    }
  };

  const updateFTERule = async (id: string, updates: Partial<FTERule>) => {
    try {
      const { data, error } = await supabase
        .from("payroll_fte_rules")
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      setFteRules((prev) => prev.map((rule) => (rule.id === id ? data : rule)));
      toast.success("FTE rule updated successfully");
      return data;
    } catch (error) {
      console.error("Error updating FTE rule:", error);
      toast.error("Failed to update FTE rule");
      throw error;
    }
  };

  const deleteFTERule = async (id: string) => {
    try {
      const { error } = await supabase
        .from("payroll_fte_rules")
        .delete()
        .eq("id", id);

      if (error) throw error;

      setFteRules((prev) => prev.filter((rule) => rule.id !== id));
      toast.success("FTE rule deleted successfully");
    } catch (error) {
      console.error("Error deleting FTE rule:", error);
      toast.error("Failed to delete FTE rule");
      throw error;
    }
  };

  const getPositionsByDepartment = () => {
    return departments.map((dept) => ({
      department: dept,
      positions: positions.filter((pos) => pos.department_id === dept.id),
    }));
  };

  const getStats = () => {
    return {
      activePositions: positions.filter((p) => p.is_active).length,
      totalDepartments: departments.filter((d) => d.is_active).length,
      totalMarkets: markets.filter((m) => m.is_active).length,
      totalFTERules: fteRules.filter((r) => r.is_active).length,
      lastUpdated: new Date().toISOString(),
    };
  };

  const resetToDefaults = async () => {
    try {
      toast.success("Reset to defaults initiated");

      await loadAllData();
    } catch (error) {
      console.error("Error resetting to defaults:", error);
      toast.error("Failed to reset to defaults");
    }
  };

  return {
    departments,
    positions,
    markets,
    salaries,
    fteRules,
    loading,

    addDepartment,
    updateDepartment,
    deleteDepartment,

    addPosition,
    updatePosition,
    deletePosition,

    addMarket,
    updateMarket,
    deleteMarket,

    upsertSalary,
    getSalaryEntry,

    addFTERule,
    updateFTERule,
    deleteFTERule,

    getPositionsByDepartment,
    getStats,
    resetToDefaults,
    refreshData: loadAllData,
  };
};
